import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';

const glowColors = [
  '#00ff9f',
  '#66ffcc',
  '#ffd700',
  '#ffe082',
  '#ff6b6b',
  '#ff8a80',
  '#64b5f6',
  '#90caf9',
  '#ba68c8',
  '#ce93d8',
  '#00e5ff',
  '#ffffff'
];

const glowShadowMap = {
  '#00ff9f': 'rgba(0, 255, 159, 0.85)',
  '#66ffcc': 'rgba(102, 255, 204, 0.75)',
  '#ffd700': 'rgba(255, 215, 0, 0.8)',
  '#ffe082': 'rgba(255, 224, 130, 0.7)',
  '#ff6b6b': 'rgba(255, 107, 107, 0.8)',
  '#ff8a80': 'rgba(255, 138, 128, 0.7)',
  '#64b5f6': 'rgba(100, 181, 246, 0.75)',
  '#90caf9': 'rgba(144, 202, 249, 0.7)',
  '#ba68c8': 'rgba(186, 104, 200, 0.75)',
  '#ce93d8': 'rgba(206, 147, 216, 0.7)',
  '#00e5ff': 'rgba(0, 229, 255, 0.75)',
  '#ffffff': 'rgba(255, 255, 255, 0.8)'
};

const buildGlowStyles = () =>
  glowColors
    .map((color) => {
      const shadow = glowShadowMap[color] || `${color}80`;
      const lower = color.toLowerCase();
      return `
        .ql-editor [style*="color: ${color}"],
        .ql-editor [style*="color: ${lower}"],
        .announcement-glow [style*="color: ${color}"],
        .announcement-glow [style*="color: ${lower}"] {
          text-shadow: 0 0 10px ${shadow};
          font-weight: 600;
        }

        .ql-color .ql-picker-item[data-value="${color}"]::before,
        .ql-background .ql-picker-item[data-value="${color}"]::before {
          background-color: ${color};
          border-radius: 3px;
          box-shadow: 0 0 8px ${shadow};
        }
      `;
    })
    .join('\n');

const resolveGlowShadow = (color, radius = '10px') => {
  if (!color) return 'none';
  const normalized = color.toLowerCase();
  const base = glowShadowMap[normalized] || glowShadowMap[color] || `${normalized}b3`;
  return `0 0 ${radius} ${base}`;
};

const AnnouncementManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    content: '',
    isActive: true,
    glowColor: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const quillRef = useRef(null);
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: glowColors }, { background: [...glowColors.slice(0, -1), 'transparent'] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean']
      ]
    }),
    []
  );
  const quillFormats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'size',
      'list',
      'bullet',
      'align',
      'link',
      'image'
    ],
    []
  );
  const glowStyles = useMemo(buildGlowStyles, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.getAnnouncements();
      const sanitizedAnnouncements = (response.announcements || []).map((item) => ({
        ...item,
        glowColor:
          typeof item.glowColor === 'string'
            ? item.glowColor.toLowerCase()
            : ''
      }));
      setAnnouncements(sanitizedAnnouncements);
    } catch (error) {
      console.error('获取公告列表失败:', error);
      showSnackbar('获取公告列表失败', 'error');
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        content: announcement.content,
        isActive: announcement.isActive,
        glowColor:
          typeof announcement.glowColor === 'string' ? announcement.glowColor.toLowerCase() : ''
      });
    } else {
      setEditingAnnouncement(null);
      setFormData({
        content: '',
        isActive: true,
        glowColor: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAnnouncement(null);
    setFormData({
      content: '',
      isActive: true,
      glowColor: ''
    });
  };

  const applyGlowColor = (color) => {
    const quillInstance = quillRef.current?.getEditor?.();
    const range = quillInstance?.getSelection();
    const hasSelection = !!range && range.length > 0;

    if (quillInstance) {
      quillInstance.format('color', color || false);
    }

    if (!hasSelection) {
      setFormData(prev => ({
        ...prev,
        glowColor: color ? color.toLowerCase() : ''
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.content.trim()) {
        showSnackbar('公告内容不能为空', 'error');
        return;
      }

      if (editingAnnouncement) {
        await api.updateAnnouncement(editingAnnouncement._id, formData);
        showSnackbar('公告更新成功');
      } else {
        await api.createAnnouncement(formData);
        showSnackbar('公告创建成功');
      }

      handleCloseDialog();
      fetchAnnouncements();
    } catch (error) {
      console.error('保存公告失败:', error);
      const errorMsg = error?.response?.data?.message || error.message || '保存公告失败';
      showSnackbar(`保存公告失败: ${errorMsg}`, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条公告吗？')) {
      return;
    }

    try {
      await api.deleteAnnouncement(id);
      showSnackbar('公告删除成功');
      fetchAnnouncements();
    } catch (error) {
      console.error('删除公告失败:', error);
      showSnackbar('删除公告失败', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          公告管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          新建公告
        </Button>
      </Box>

      <Paper sx={{ 
        backgroundColor: 'rgba(0, 30, 60, 0.8)', 
        backdropFilter: 'blur(10px)',
        borderRadius: 2
      }}>
        <List>
          {announcements.map((announcement) => (
            <ListItem
              key={announcement._id}
              sx={{
                borderBottom: '1px solid rgba(0, 255, 159, 0.1)',
                '&:last-child': { borderBottom: 'none' }
              }}
              secondaryAction={
                <Box>
                  <IconButton 
                    edge="end" 
                    aria-label="edit"
                    onClick={() => handleOpenDialog(announcement)}
                    sx={{ color: 'primary.main', mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    edge="end" 
                    aria-label="delete"
                    onClick={() => handleDelete(announcement._id)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <Box sx={{ flex: 1 }}>
                <Box
                  className="announcement-glow"
                      sx={{ 
                        color: announcement.glowColor || (announcement.isActive ? 'text.primary' : 'text.secondary'),
                        textDecoration: announcement.isActive ? 'none' : 'line-through',
                        textShadow: announcement.glowColor ? resolveGlowShadow(announcement.glowColor) : 'none',
                        fontWeight: announcement.glowColor ? 600 : 'inherit',
                        whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    '& p': { margin: 0, marginBottom: 1.5 },
                    '& p:last-of-type': { marginBottom: 0 }
                      }}
                  dangerouslySetInnerHTML={{ __html: announcement.content }}
                />
                    {!announcement.isActive && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                      display: 'inline-block',
                      mt: 1,
                          color: 'text.secondary',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}
                      >
                        已停用
                      </Typography>
                    )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    创建时间: {new Date(announcement.createdAt).toLocaleString()}
                  {announcement.updatedAt && announcement.updatedAt !== announcement.createdAt && ` | 更新时间: ${new Date(announcement.updatedAt).toLocaleString()}`}
                  </Typography>
              </Box>
            </ListItem>
          ))}
          {announcements.length === 0 && (
            <ListItem>
              <Typography color="text.secondary" align="center" sx={{ width: '100%', py: 2 }}>
                    暂无公告
                  </Typography>
            </ListItem>
          )}
        </List>
      </Paper>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle>
          {editingAnnouncement ? '编辑公告' : '新建公告'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              公告内容（支持富文本格式）
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                光效颜色（可选）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant={formData.glowColor === '' ? 'contained' : 'outlined'}
                  onClick={() => applyGlowColor('')}
                >
                  关闭光效
                </Button>
                {glowColors.map((color) => (
                  <Button
                    key={color}
                    size="small"
                    variant={formData.glowColor === color ? 'contained' : 'outlined'}
                    onClick={() => applyGlowColor(color)}
                    sx={{
                      color: formData.glowColor === color ? '#001e3c' : color,
                      backgroundColor: formData.glowColor === color ? color : 'rgba(255,255,255,0.08)',
                      borderColor: color,
                      boxShadow:
                        formData.glowColor === color
                          ? resolveGlowShadow(color, '12px')
                          : resolveGlowShadow(color, '6px'),
                      '&:hover': {
                        backgroundColor: color,
                        boxShadow: resolveGlowShadow(color, '12px')
                      }
                    }}
                  >
                    {color.toUpperCase()}
                  </Button>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                提示：选中需要发光的文字后点击颜色按钮，可仅为该部分设置发光。未选中文字时点击则设置整篇默认发光颜色。
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px dashed rgba(255,255,255,0.2)',
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 0.5,
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  光效预览
                </Typography>
                <Typography
                  className="announcement-glow"
                  sx={{
                    color: formData.glowColor || 'rgba(255,255,255,0.8)',
                    textShadow: resolveGlowShadow(formData.glowColor, '12px'),
                    fontWeight: formData.glowColor ? 700 : 500
                  }}
                >
                  {formData.glowColor ? '公告字体将呈现发光效果' : '当前未启用光效'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              '& .quill': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 1
              },
              '& .ql-toolbar': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderBottom: 'none'
              },
              '& .ql-container': {
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderTop: 'none',
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                fontSize: '14px',
                minHeight: '200px'
              },
              '& .ql-editor': {
                color: 'rgba(255, 255, 255, 0.9)',
                minHeight: '200px'
              },
              '& .ql-stroke': {
                stroke: 'rgba(255, 255, 255, 0.7)'
              },
              '& .ql-fill': {
                fill: 'rgba(255, 255, 255, 0.7)'
              },
              '& .ql-picker-label': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .ql-picker-options': {
                backgroundColor: 'rgba(0, 30, 60, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              },
              '& .ql-toolbar .ql-formats button svg': {
                filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))'
              },
              '& .ql-color .ql-picker-item::before, & .ql-background .ql-picker-item::before': {
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }
            }}>
              <style>{glowStyles}</style>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                modules={quillModules}
                formats={quillFormats}
                ref={quillRef}
              />
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                color="primary"
              />
            }
            label="启用公告"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSubmit} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnnouncementManagement; 